import type { DirectorySuggestionItem, FilterOptions, MarkerData } from './types';

type Bounds = { north: number; south: number; east: number; west: number };

const COUNTRY_BOUNDS: Record<string, Bounds> = {
    ch: { north: 47.85, south: 45.75, east: 10.65, west: 5.75 },
    fr: { north: 51.20, south: 41.20, east: 9.80, west: -5.50 },
    be: { north: 51.60, south: 49.45, east: 6.45, west: 2.45 },
    lu: { north: 50.25, south: 49.40, east: 6.60, west: 5.70 },
    de: { north: 55.20, south: 47.20, east: 15.20, west: 5.70 },
    it: { north: 47.20, south: 35.40, east: 18.70, west: 6.40 },
    at: { north: 49.10, south: 46.30, east: 17.30, west: 9.40 },
    li: { north: 47.30, south: 47.00, east: 9.70, west: 9.45 },
    mc: { north: 43.80, south: 43.70, east: 7.50, west: 7.35 },
    ca: { north: 84.10, south: 41.50, east: -52.50, west: -141.10 },
};

function isInsideCountry(lat: number, lng: number, country: string): boolean {
    const bounds = COUNTRY_BOUNDS[country];
    if (!bounds) return true;
    return lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
}

export function isValidDirectoryMarker(marker: MarkerData, allowedCountries: string[]): boolean {
    const lat = Number(marker.lat);
    const lng = Number(marker.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return false;
    }

    const allowed = allowedCountries.map(country => country.toLowerCase());
    const declared = String(marker.country ?? '').trim().toLowerCase();
    if (declared) {
        return (allowed.length === 0 || allowed.includes(declared)) && isInsideCountry(lat, lng, declared);
    }

    if (allowed.length === 0) return true;
    return allowed.some(country => isInsideCountry(lat, lng, country));
}

export function normalizeDirectoryMarkers(markers: MarkerData[], allowedCountries: string[]): MarkerData[] {
    return markers.filter(marker => isValidDirectoryMarker(marker, allowedCountries));
}

export function memberKey(member: MarkerData): string {
    const userId = Number(member.wp_user_id);
    if (Number.isInteger(userId) && userId > 0) return `user:${userId}`;

    // member_id (hypno) avant l’URL : plus stable multi-adresses
    const memberId = Number(member.member_id);
    if (Number.isInteger(memberId) && memberId > 0) return `member:${memberId}`;

    const profileUrl = String(member.aph_member_url ?? '').trim();
    if (profileUrl) return `url:${profileUrl}`;

    return `fallback:${[
        member.prenom,
        member.nom,
        member.prenom_nom,
        member.nom_cabinet,
        // Pas d’adresse/lat dans la clé identité : un thérapeute multi-cabinets
        // reste une seule personne pour les compteurs / liste.
        member.npa,
        member.localite,
    ].map(value => String(value ?? '').trim().toLowerCase()).join('|')}`;
}

/** Nombre de thérapeutes uniques (multi-adresses = 1 personne). */
export function countUniqueMembers(markers: MarkerData[]): number {
    const seen = new Set<string>();
    for (const marker of markers) {
        seen.add(memberKey(marker));
    }
    return seen.size;
}

export function buildMemberOrder(markers: MarkerData[]): Map<string, number> {
    const order = new Map<string, number>();
    markers.forEach(marker => {
        const key = memberKey(marker);
        if (!order.has(key)) order.set(key, order.size + 1);
    });
    return order;
}

export function memberInitials(member: MarkerData): string {
    const parts = [member.prenom, member.nom]
        .map(value => String(value ?? '').trim())
        .filter(Boolean);
    if (parts.length === 0) {
        parts.push(...String(member.prenom_nom ?? member.display_name ?? member.nom_cabinet ?? '')
            .trim()
            .split(/\s+/)
            .filter(Boolean));
    }
    return parts.slice(0, 2).map(part => part.charAt(0).toLocaleUpperCase()).join('') || '•';
}

export type DirectoryResultsState = 'initial' | 'pending-viewport' | 'zoom' | 'no-results' | 'results' | 'empty';

interface DirectoryResultsStateInput {
    showMarkers: boolean;
    viewportZoom: number | null;
    listResultsMinZoom: number;
    searchActive: boolean;
    markerCount: number;
    visibleMemberCount: number;
}

export function deriveDirectoryResultsState(input: DirectoryResultsStateInput): DirectoryResultsState {
    if (!input.showMarkers) return 'initial';
    // Marqueurs déjà débloqués mais MapLibre n'a pas encore publié de viewport :
    // éviter le flash « aucun résultat » au premier paint.
    if (input.viewportZoom === null) return 'pending-viewport';
    if (input.viewportZoom < input.listResultsMinZoom) return 'zoom';
    if (input.searchActive && input.markerCount === 0) return 'no-results';
    if (input.visibleMemberCount > 0) return 'results';
    return 'empty';
}

function normalizeSearchText(value: string): string {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
}

export function buildFacetSuggestions(
    query: string,
    options: FilterOptions,
    groupLabels: Record<string, string>,
    limit = 8,
): DirectorySuggestionItem[] {
    const needle = normalizeSearchText(query);
    if (needle.length < 3) return [];

    const entries = [
        ...(options.specialisationsByCat ?? []).flatMap(category => category.options.map(option => ({
            ...option,
            field: 'specialisations' as const,
            groupLabel: category.cat_label || groupLabels.specialisations,
        }))),
        ...(options.langues ?? []).map(option => ({ ...option, field: 'langues' as const, groupLabel: groupLabels.langues })),
        ...(options.champs_hypnose ?? []).map(option => ({ ...option, field: 'champs_hypnose' as const, groupLabel: groupLabels.champs_hypnose })),
        ...(options.publics_privilegies ?? []).map(option => ({ ...option, field: 'publics_privilegies' as const, groupLabel: groupLabels.publics_privilegies })),
        ...(options.pratiques_complementaires ?? []).map(option => ({ ...option, field: 'pratiques_complementaires' as const, groupLabel: groupLabels.pratiques_complementaires })),
    ];

    return entries
        .map(entry => {
            const label = normalizeSearchText(entry.label);
            const wordPrefix = label.split(/\s+/).some(word => word.startsWith(needle));
            const score = label.startsWith(needle) ? 0 : wordPrefix ? 1 : label.includes(needle) ? 2 : 99;
            return { entry, score };
        })
        .filter(match => match.score < 99)
        .sort((a, b) => a.score - b.score || a.entry.label.localeCompare(b.entry.label))
        .slice(0, limit)
        .map(({ entry }) => ({
            value: entry.label,
            type: 'filter' as const,
            field: entry.field,
            slug: entry.slug,
            groupLabel: entry.groupLabel,
        }));
}

export interface ViewportMembersResult {
    /** Membres uniques réellement dans la bbox (avant plafonnement d'affichage). */
    inViewport: MarkerData[];
    /** Sous-ensemble affiché dans la liste (≤ max_visible_results). */
    displayed: MarkerData[];
    inViewportCount: number;
    displayedCount: number;
}

/**
 * Calcule les membres présents dans le viewport, avec déduplication et
 * plafonnement d'affichage séparé du décompte zone.
 */
export function selectViewportMembers(
    markers: MarkerData[],
    bbox: [number, number, number, number],
    options: {
        sortOrder?: string;
        maxVisibleResults?: number;
    } = {},
): ViewportMembersResult {
    const [west, south, east, north] = bbox;
    const seen = new Set<string>();
    const inViewport: MarkerData[] = [];
    for (const m of markers) {
        const lng = Number(m.lng);
        const lat = Number(m.lat);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
        if (lng < west || lng > east || lat < south || lat > north) continue;
        const key = memberKey(m);
        if (seen.has(key)) continue;
        seen.add(key);
        inViewport.push(m);
    }

    if (options.sortOrder === 'by_distance') {
        const cx = (west + east) / 2;
        const cy = (south + north) / 2;
        inViewport.sort((a, b) => {
            const da = Math.hypot(Number(a.lng) - cx, Number(a.lat) - cy);
            const db = Math.hypot(Number(b.lng) - cx, Number(b.lat) - cy);
            return da - db;
        });
    }

    const max = options.maxVisibleResults ?? 0;
    const displayed = max > 0 && inViewport.length > max
        ? inViewport.slice(0, max)
        : inViewport;

    return {
        inViewport,
        displayed,
        inViewportCount: inViewport.length,
        displayedCount: displayed.length,
    };
}
